import {
  deleteEmail,
  deleteMailingList,
  getEmailsForMailingList,
  updateAllContactFieldsForMailingListAudience,
  updateMailingListName,
  getUserMailingListsForSignUpForms,
} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-thunk';
import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import type {AudienceMapDataPayload, AudienceState} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-reducer';
import {
  doUpdateCreateSignUpFormPanelLists,
  doUpdateAudienceDataForMailingListAudience,
  addAudience,
  addAudienceIdsToMailingList,
  addMailingList,
  changeMailingListName,
  removeAudience,
  removeMailingList,
} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-reducer';
import type {UserSignupFormMailingListData} from '@Panels/create-signup-form-panel/create-signup-form-panel.types';
import {getEmailStatesEnumValues} from '@Libraries/email-marketing-campaigns-audience-library';
import type {AudienceMailingList} from '@Components/mailing-list-wizard/mailing-list-wizard.types';

export const emailMarketingCampaignAudienceExtraReducers = (builder: ActionReducerMapBuilder<AudienceState>): void => {
  builder.addCase(getEmailsForMailingList.fulfilled, (state, {payload}) => {
    const audienceIds: number[] = [];
    payload.audiences.forEach((item) => {
      addAudience(state, item);
      audienceIds.push(item.id);
    });

    if (!state.mailingLists[payload.idMailingList]) {
      const getEmailsForMailingListParams: AudienceMailingList = {
        emailIds: audienceIds,
        id: payload.idMailingList,
        name: payload.name,
        totalEmailsCount: payload.count,
        filters: getEmailStatesEnumValues().reduce((obj, emailState) => {
          return {...obj, [emailState]: true};
        }, {}),
        searchTerm: '',
        isSpam: payload.isSpam,
        lastViewed: payload.lastViewed,
        isSmart: payload.isSmart,
        isSharedInTeam: payload.isSharedInTeam,
        idOwner: payload.idOwner
      };

      addMailingList(state, payload.idMailingList, getEmailsForMailingListParams);
    } else {
      addAudienceIdsToMailingList(state, payload.idMailingList, audienceIds);
    }
  });

  builder.addCase(deleteEmail.fulfilled, (state, {meta}) => {
    const {audience, idMailingList, index} = meta.arg;
    const idAudience = audience.id;

    removeAudience(state, idMailingList, idAudience, index);
  });

  builder.addCase(updateMailingListName.pending, (state, {meta}) => {
    const {idMailingList, newName} = meta.arg;
    changeMailingListName(state, idMailingList, newName);
  });

  builder.addCase(updateMailingListName.rejected, (state, {meta}) => {
    const {idMailingList, currentName} = meta.arg;
    changeMailingListName(state, idMailingList, currentName);
  });

  builder.addCase(deleteMailingList.fulfilled, (state, {meta}) => {
    const {idMailingList} = meta.arg;
    removeMailingList(state, idMailingList);
  });

  builder.addCase(updateAllContactFieldsForMailingListAudience.fulfilled, (state, {meta}) => {
    const {arg} = meta;
    doUpdateAudienceDataForMailingListAudience(state, arg as AudienceMapDataPayload);
  });

  builder.addCase(getUserMailingListsForSignUpForms.fulfilled, (state, {payload}) => {
    doUpdateCreateSignUpFormPanelLists(
      state,
      payload.mailingLists.map((mailingList): UserSignupFormMailingListData => {
        return {
          id: mailingList.id,
          name: mailingList.name,
          numNonDeletedEmails: mailingList.numNonDeletedEmails,
          lastUpdatedOn: mailingList.lastUpdatedOn,
          isSharedInTeam: mailingList.isSharedInTeam,
          idOwner: mailingList.idOwner,
        };
      })
    );
  });
};
